import { supabase } from '../client';
import { Client } from '@core/types';

export type ClientSortColumn = 'name' | 'created_at' | 'industry' | 'contact_email';

export interface ClientListOptions {
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: ClientSortColumn;
  /** Default true for name/industry/contact_email; false for created_at */
  sortAsc?: boolean;
}

export interface ClientListResult {
  data: Client[];
  total: number;
}

export const clientService = {
  /**
   * Returns an existing client in the org with the same contact email (case-insensitive), if any.
   * Used to block accidental duplicate onboarding when the same email is submitted repeatedly.
   */
  async findExistingByContactEmail(orgId: string, email: string | null | undefined): Promise<Client | null> {
    const e = email?.trim();
    if (!e) return null;
    const { data, error } = await supabase
      .from('ggj_b2b_clients')
      .select('*')
      .eq('org_id', orgId)
      .ilike('contact_email', e)
      .limit(1);
    if (error) throw error;
    const row = data?.[0];
    return row ? (row as Client) : null;
  },

  async list(
    orgId: string,
    searchOrOptions?: string | ClientListOptions
  ): Promise<Client[] | ClientListResult> {
    const options: ClientListOptions =
      typeof searchOrOptions === 'string' ? { search: searchOrOptions } : searchOrOptions ?? {};

    const sortCol: ClientSortColumn = options.sortBy ?? 'name';
    const defaultAsc = sortCol === 'created_at' ? false : true;
    const ascending = options.sortAsc ?? defaultAsc;

    let query = supabase
      .from('ggj_b2b_clients')
      .select('*', { count: options.pageSize ? 'exact' : undefined })
      .eq('org_id', orgId)
      .order(sortCol, { ascending, nullsFirst: false });

    if (options.search) {
      query = query.or(`name.ilike.%${options.search}%,industry.ilike.%${options.search}%`);
    }

    if (options.pageSize != null) {
      const page = Math.max(1, options.page ?? 1);
      const from = (page - 1) * options.pageSize;
      const to = from + options.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    if (options.pageSize != null) {
      return { data: data ?? [], total: count ?? 0 };
    }
    return data ?? [];
  },

  async getById(id: string): Promise<Client | null> {
    const { data } = await supabase
      .from('ggj_b2b_clients')
      .select('*')
      .eq('id', id)
      .single();
    return data;
  },

  async create(payload: Omit<Client, 'id' | 'created_at'>): Promise<Client> {
    if (payload.contact_email?.trim()) {
      const dup = await this.findExistingByContactEmail(payload.org_id, payload.contact_email);
      if (dup) {
        throw new Error(
          `A client with contact email "${payload.contact_email.trim()}" already exists (${dup.name ?? 'unnamed'}). Open that record instead of creating another.`,
        );
      }
    }
    const { data, error } = await supabase
      .from('ggj_b2b_clients')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id: string, payload: Partial<Client>): Promise<Client> {
    const { data, error } = await supabase
      .from('ggj_b2b_clients')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Deletes one client row. Postgres cascades to dependent rows (jobs, applications,
   * requisitions, contacts, notes, compliance docs, etc.) via ON DELETE CASCADE on those FKs.
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_clients').delete().eq('id', id);
    if (error) throw error;
  },
};
